import { useState } from 'react'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import { InputText } from '#app/components/ui/input-text'

interface ProyectoData {
	nombreContacto: string
	email: string
	telefono: string
	tituloContrato: string
	seccionesProyecto: string
	ubicacionObra: string
	propietarioComitente: string
	contratistaPrincipal: string
	subcontratistas: string
	ingenieroConsultor: string
}

interface TodoRiesgoConstruccionProyectoScreenProps {
	onNext: (data: ProyectoData) => void
	onBack: () => void
	data?: Partial<ProyectoData>
}

export function TodoRiesgoConstruccionProyectoScreen({
	onNext,
	onBack,
	data,
}: TodoRiesgoConstruccionProyectoScreenProps) {
	const [formData, setFormData] = useState<ProyectoData>({
		nombreContacto: data?.nombreContacto || '',
		email: data?.email || '',
		telefono: data?.telefono || '',
		tituloContrato: data?.tituloContrato || '',
		seccionesProyecto: data?.seccionesProyecto || '',
		ubicacionObra: data?.ubicacionObra || '',
		propietarioComitente: data?.propietarioComitente || '',
		contratistaPrincipal: data?.contratistaPrincipal || '',
		subcontratistas: data?.subcontratistas || '',
		ingenieroConsultor: data?.ingenieroConsultor || '',
	})

	const [errors, setErrors] = useState<
		Partial<Record<keyof ProyectoData, string>>
	>({})

	const validateForm = (): boolean => {
		const newErrors: Partial<Record<keyof ProyectoData, string>> = {}

		if (!formData.nombreContacto.trim()) {
			newErrors.nombreContacto = 'El nombre de contacto es requerido'
		}
		if (!formData.email.trim()) {
			newErrors.email = 'El email es requerido'
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
			newErrors.email = 'El email no es válido'
		}
		if (!formData.telefono.trim()) {
			newErrors.telefono = 'El teléfono es requerido'
		}
		if (!formData.tituloContrato.trim()) {
			newErrors.tituloContrato = 'El título del contrato es requerido'
		}
		if (!formData.ubicacionObra.trim()) {
			newErrors.ubicacionObra = 'La ubicación de la obra es requerida'
		}
		if (!formData.propietarioComitente.trim()) {
			newErrors.propietarioComitente =
				'Los datos del propietario son requeridos'
		}
		if (!formData.contratistaPrincipal.trim()) {
			newErrors.contratistaPrincipal =
				'Los datos del contratista son requeridos'
		}

		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const handleSubmit = () => {
		if (validateForm()) {
			onNext(formData)
		}
	}

	return (
		<div className="flex h-full w-full flex-col">
			<div className="flex-1 overflow-y-auto">
				<div className="flex w-full flex-col items-center gap-4">
					<h2 className="font-parkinsans text-center text-2xl leading-8 font-medium text-blue-800">
						Proyecto y Partes
					</h2>
					<p className="text-center text-sm text-neutral-600">
						Información del proyecto y las partes involucradas
					</p>

					<div className="flex w-full flex-col gap-4">
						<div className="rounded-xl bg-blue-50 p-3">
							<p className="font-inter text-xs font-medium text-blue-800">
								Datos de Contacto
							</p>
						</div>

						<InputText
							label="Nombre de Contacto"
							labelPosition="internal"
							size="l"
							placeholder="Nombre completo"
							value={formData.nombreContacto}
							onChange={(e) =>
								setFormData({ ...formData, nombreContacto: e.target.value })
							}
							errorText={errors.nombreContacto}
						/>

						<div className="grid grid-cols-2 gap-4">
							<InputText
								label="Email"
								labelPosition="internal"
								size="l"
								placeholder="correo@ejemplo.com"
								type="email"
								value={formData.email}
								onChange={(e) =>
									setFormData({ ...formData, email: e.target.value })
								}
								errorText={errors.email}
							/>

							<InputText
								label="Teléfono"
								labelPosition="internal"
								size="l"
								placeholder="11 1234-5678"
								value={formData.telefono}
								onChange={(e) =>
									setFormData({ ...formData, telefono: e.target.value })
								}
								errorText={errors.telefono}
							/>
						</div>

						<div className="rounded-xl bg-blue-50 p-3">
							<p className="font-inter text-xs font-medium text-blue-800">
								Datos del Proyecto
							</p>
						</div>

						<InputText
							label="Título del Contrato"
							labelPosition="internal"
							size="l"
							placeholder="Nombre del proyecto de construcción"
							value={formData.tituloContrato}
							onChange={(e) =>
								setFormData({ ...formData, tituloContrato: e.target.value })
							}
							errorText={errors.tituloContrato}
						/>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Secciones del Proyecto (opcional)
							</label>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
								rows={2}
								placeholder="Si el proyecto tiene varias secciones, descríbalas aquí"
								value={formData.seccionesProyecto}
								onChange={(e) =>
									setFormData({
										...formData,
										seccionesProyecto: e.target.value,
									})
								}
							/>
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Ubicación de la Obra
							</label>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
								rows={2}
								placeholder="País, provincia, ciudad, calle y número"
								value={formData.ubicacionObra}
								onChange={(e) =>
									setFormData({ ...formData, ubicacionObra: e.target.value })
								}
							/>
							{errors.ubicacionObra && (
								<p className="text-sm text-red-500">{errors.ubicacionObra}</p>
							)}
						</div>

						<div className="rounded-xl bg-blue-50 p-3">
							<p className="font-inter text-xs font-medium text-blue-800">
								Partes Involucradas
							</p>
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Propietario / Comitente
							</label>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
								rows={2}
								placeholder="Nombre y dirección del propietario"
								value={formData.propietarioComitente}
								onChange={(e) =>
									setFormData({
										...formData,
										propietarioComitente: e.target.value,
									})
								}
							/>
							{errors.propietarioComitente && (
								<p className="text-sm text-red-500">
									{errors.propietarioComitente}
								</p>
							)}
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Contratista Principal
							</label>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
								rows={2}
								placeholder="Nombre y dirección del contratista principal"
								value={formData.contratistaPrincipal}
								onChange={(e) =>
									setFormData({
										...formData,
										contratistaPrincipal: e.target.value,
									})
								}
							/>
							{errors.contratistaPrincipal && (
								<p className="text-sm text-red-500">
									{errors.contratistaPrincipal}
								</p>
							)}
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Subcontratistas (opcional)
							</label>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
								rows={2}
								placeholder="Nombres y direcciones de subcontratistas"
								value={formData.subcontratistas}
								onChange={(e) =>
									setFormData({ ...formData, subcontratistas: e.target.value })
								}
							/>
						</div>

						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium text-neutral-700">
								Ingeniero Consultor (opcional)
							</label>
							<textarea
								className="w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm"
								rows={2}
								placeholder="Nombre y dirección del ingeniero consultor"
								value={formData.ingenieroConsultor}
								onChange={(e) =>
									setFormData({
										...formData,
										ingenieroConsultor: e.target.value,
									})
								}
							/>
						</div>
					</div>
				</div>
			</div>

			<div className="flex w-full shrink-0 flex-col items-center justify-start gap-2 bg-white pt-4 md:flex-row md:justify-between md:bg-transparent md:pt-8">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="order-2 w-full gap-2 rounded-xl px-4 py-3 text-sm leading-4 md:order-1 md:w-auto md:px-6 md:py-4 md:text-base md:leading-5"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="secondary"
					appearance="solid"
					size="l"
					onClick={handleSubmit}
					className="order-1 w-full md:order-2 md:w-auto"
				>
					Siguiente
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>
		</div>
	)
}
